import { getCountyInsights } from "@/lib/county-insights.functions";
import { getState as getStateForCounty } from "@/data/states";
import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { findCity } from "@/data/investor-cities";
import { getMarketStats } from "@/lib/market-stats.functions";
import { ConstructionCityPage } from "@/components/site/ConstructionCityPage";
import { constructionCityFaqJsonLd, constructionCityMetaDescription } from "@/components/site/ConstructionCityPage.seo";
import { breadcrumbJsonLd, productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { localBusinessJsonLd, serviceJsonLd, speakableJsonLd } from "@/lib/seo-schema";
import { cityKeywordsMeta } from "@/lib/page-keywords";

const SLUG = "construction-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/construction-loans/$state/$city")({
  loader: async ({ params }) => {
    const city = findCity(params.state, params.city);
    if (!city) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: city.county,
        placeLabel: `${city.name}, ${city.stateName}`,
      },
    }).catch(() => null);
    const liveStats = await getMarketStats({
      data: { stateSlug: params.state, citySlug: params.city },
    }).catch(() => null);
    return { city, liveStats , countyInsights };
  },
  head: ({ params, loaderData }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}/${params.city}`;
    const hero = getCityHero(params.state, params.city, city.name, city.stateName) ?? HERO;
    const title = fitTitle(`${city.name}, ${city.stateName} Construction Loans | Ground-Up Builder Financing | Simply Approved`);
    const description = fitDescription(constructionCityMetaDescription(city.name, city.stateName));
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: cityKeywordsMeta("construction-loans", city.name, city.stateName) },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        { property: "og:image", content: absoluteUrl(hero.src) },
        { property: "og:image:alt", content: hero.alt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: absoluteUrl(hero.src) },
        { property: "article:published_time", content: "2026-01-01" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "preload", as: "image", href: hero.src },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(constructionCityFaqJsonLd(city.name, city.stateName)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(localBusinessJsonLd({ url, state: city.stateName, city: city.name })),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceJsonLd({
            url,
            serviceName: `Ground-Up Construction Loans in ${city.name}, ${city.stateName}`,
            description,
            state: city.stateName,
            city: city.name,
          })),
        },
        { type: "application/ld+json", children: JSON.stringify(speakableJsonLd(url)) },
        { type: "application/ld+json", children: JSON.stringify(productHowToJsonLd(PRODUCT, url)) },
        { type: "application/ld+json", children: JSON.stringify(productArticleJsonLd(PRODUCT, url)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", url: "/" },
              { name: PRODUCT.shortName, url: `/${SLUG}` },
              { name: city.stateName, url: `/${SLUG}/${params.state}` },
              { name: city.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { city, liveStats, countyInsights } = Route.useLoaderData();
  return (
    <ConstructionCityPage
      product={PRODUCT}
      city={city}
      liveStats={liveStats}
      countyInsights={countyInsights}
      midSlot={<ProductReportCtaBanner productKey="construction" variant="full" locationLabel={`${city.name}, ${city.stateName}`} />}
    />
  );
}
